import matter from 'gray-matter';
export function parseProjectFile(content) {
    const { data, content: body } = matter(content);
    const sections = new Map();
    const lines = body.split('\n');
    let currentSection = '';
    let currentContent = [];
    for (const line of lines) {
        if (line.startsWith('## ')) {
            if (currentSection) {
                sections.set(currentSection, currentContent.join('\n').trim());
            }
            currentSection = line.replace('## ', '').trim();
            currentContent = [];
        }
        else if (currentSection) {
            currentContent.push(line);
        }
    }
    if (currentSection) {
        sections.set(currentSection, currentContent.join('\n').trim());
    }
    return {
        frontMatter: data,
        sections,
        raw: content,
    };
}
export function parseOverview(content) {
    return content;
}
export function parseTeam(content) {
    const members = [];
    const lines = content.split('\n');
    const inTable = false;
    for (const line of lines) {
        if (line.startsWith('|') && !line.includes('---')) {
            const cells = line
                .split('|')
                .map(c => c.trim())
                .filter(c => c);
            if (cells.length >= 3 && !cells[0].includes('Name')) {
                members.push({
                    name: cells[0],
                    role: cells[1],
                    username: cells[2],
                    availability: cells[3] || undefined,
                    wip_limit: cells[4] ? parseInt(cells[4]) : undefined,
                });
            }
        }
    }
    return members;
}
export function parseBacklogKanban(content) {
    const tasks = [];
    const lines = content.split('\n');
    for (const line of lines) {
        if (line.startsWith('|') && !line.includes('---') && !line.includes('ID')) {
            const cells = line
                .split('|')
                .map(c => c.trim())
                .filter(c => c);
            if (cells.length >= 6) {
                tasks.push({
                    id: cells[0],
                    title: cells[1],
                    priority: cells[2],
                    storyPoints: cells[3] ? parseInt(cells[3]) : undefined,
                    status: cells[4],
                    assignee: cells[5] || undefined,
                    labels: [],
                    created: new Date().toISOString(),
                    updated: new Date().toISOString(),
                    description: '',
                    acceptanceCriteria: [],
                    timeLog: [],
                    comments: [],
                });
            }
        }
    }
    return tasks;
}
export function parseBacklogScrum(content) {
    const epics = [];
    const lines = content.split('\n');
    let currentEpic = null;
    for (const line of lines) {
        if (line.startsWith('### EPIC-')) {
            if (currentEpic) {
                epics.push(currentEpic);
            }
            const match = line.match(/### (EPIC-\d+):\s*(.+)/);
            if (match) {
                currentEpic = {
                    id: match[1],
                    title: match[2],
                    priority: 'medium',
                    status: 'backlog',
                    storyPoints: 0,
                    userStories: [],
                };
            }
        }
        else if (line.startsWith('- [ ] US-') || line.startsWith('- [x] US-')) {
            if (currentEpic) {
                const completed = line.startsWith('- [x]');
                const match = line.match(/US-\d+:\s*(.+)/);
                if (match) {
                    currentEpic.userStories.push({
                        id: `US-${currentEpic.userStories.length + 1}`,
                        description: match[1],
                        completed,
                    });
                }
            }
        }
    }
    if (currentEpic) {
        epics.push(currentEpic);
    }
    return epics;
}
export function parseSprints(content) {
    const sprints = [];
    const lines = content.split('\n');
    let currentSprint = null;
    for (const line of lines) {
        if (line.startsWith('### Sprint ')) {
            if (currentSprint) {
                sprints.push(currentSprint);
            }
            const match = line.match(/### (Sprint \d+):\s*(.+)/);
            if (match) {
                currentSprint = {
                    id: match[1].toLowerCase().replace(/\s+/g, '-'),
                    name: match[1] + ': ' + match[2],
                    startDate: '',
                    endDate: '',
                    status: 'planned',
                    goal: '',
                    committedPoints: 0,
                    completedPoints: 0,
                    velocity: 0,
                    committedTasks: [],
                };
            }
        }
        else if (currentSprint && line.includes('Start Date')) {
            const match = line.match(/\|\s*Start Date\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentSprint.startDate = match[1];
        }
        else if (currentSprint && line.includes('End Date')) {
            const match = line.match(/\|\s*End Date\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentSprint.endDate = match[1];
        }
        else if (currentSprint && line.includes('Status')) {
            const match = line.match(/\|\s*Status\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentSprint.status = match[1];
        }
        else if (currentSprint && line.includes('Committed Points')) {
            const match = line.match(/\|\s*Committed Points\s*\|\s*(\d+)\s*\|/);
            if (match)
                currentSprint.committedPoints = parseInt(match[1]);
        }
        else if (currentSprint && line.includes('Completed Points')) {
            const match = line.match(/\|\s*Completed Points\s*\|\s*(\d+)\s*\|/);
            if (match)
                currentSprint.completedPoints = parseInt(match[1]);
        }
        else if (currentSprint && line.includes('Sprint Goal:')) {
            currentSprint.goal = line.replace('**Sprint Goal:**', '').trim();
        }
    }
    if (currentSprint) {
        sprints.push(currentSprint);
    }
    return sprints;
}
export function parseTasks(content) {
    const tasks = [];
    const lines = content.split('\n');
    let currentTask = null;
    let inDescription = false;
    let descriptionLines = [];
    for (const line of lines) {
        if (/^### .+?:/.test(line) && !line.startsWith('### Sprint') && !line.startsWith('### EPIC-')) {
            if (currentTask) {
                if (descriptionLines.length > 0) {
                    currentTask.description = descriptionLines.join('\n').trim();
                    descriptionLines = [];
                }
                tasks.push(currentTask);
            }
            inDescription = false;
            const match = line.match(/### (.+?):\s*(.+)/);
            if (match) {
                currentTask = {
                    id: match[1],
                    title: match[2].replace(/\s*\{#.+?\}\s*$/, '').trim(),
                    status: 'backlog',
                    priority: 'medium',
                    labels: [],
                    created: new Date().toISOString(),
                    updated: new Date().toISOString(),
                    description: '',
                    acceptanceCriteria: [],
                    timeLog: [],
                    comments: [],
                };
            }
        }
        else if (currentTask && line.trim() === '#### Description') {
            inDescription = true;
            descriptionLines = [];
        }
        else if (currentTask && line.trim() === '#### Acceptance Criteria') {
            inDescription = false;
        }
        else if (currentTask && line === '---') {
            inDescription = false;
        }
        else if (currentTask && inDescription) {
            descriptionLines.push(line);
        }
        else if (currentTask && line.includes('Status')) {
            const match = line.match(/\|\s*Status\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.status = match[1];
        }
        else if (currentTask && line.includes('Priority')) {
            const match = line.match(/\|\s*Priority\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.priority = match[1];
        }
        else if (currentTask && line.includes('Assignee')) {
            const match = line.match(/\|\s*Assignee\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.assignee = match[1];
        }
        else if (currentTask && line.includes('Story Points')) {
            const match = line.match(/\|\s*Story Points\s*\|\s*(\d+)\s*\|/);
            if (match)
                currentTask.storyPoints = parseInt(match[1]);
        }
        else if (currentTask && line.includes('Sprint')) {
            const match = line.match(/\|\s*Sprint\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.sprint = match[1];
        }
        else if (currentTask && line.includes('Labels')) {
            const match = line.match(/\|\s*Labels\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.labels = match[1].split(',').map(l => l.trim());
        }
        else if (currentTask && line.includes('Created')) {
            const match = line.match(/\|\s*Created\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.created = match[1];
        }
        else if (currentTask && line.includes('Updated')) {
            const match = line.match(/\|\s*Updated\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.updated = match[1];
        }
        else if (currentTask && line.includes('Started')) {
            const match = line.match(/\|\s*Started\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.started = match[1];
        }
        else if (currentTask && line.includes('Last Updated By')) {
            const match = line.match(/\|\s*Last Updated By\s*\|\s*(.+?)\s*\|/);
            if (match)
                currentTask.lastUpdatedBy = match[1];
        }
        else if (currentTask && line.startsWith('- [ ]')) {
            currentTask.acceptanceCriteria.push({
                text: line.replace('- [ ] ', ''),
                completed: false,
            });
        }
        else if (currentTask && line.startsWith('- [x]')) {
            currentTask.acceptanceCriteria.push({
                text: line.replace('- [x] ', ''),
                completed: true,
            });
        }
    }
    if (currentTask) {
        if (descriptionLines.length > 0) {
            currentTask.description = descriptionLines.join('\n').trim();
        }
        tasks.push(currentTask);
    }
    return tasks;
}
export function parseChangeLog(content) {
    const entries = [];
    const lines = content.split('\n');
    for (const line of lines) {
        if (line.startsWith('|') && !line.includes('---') && !line.includes('#')) {
            const cells = line
                .split('|')
                .map(c => c.trim())
                .filter(c => c);
            if (cells.length >= 6) {
                entries.push({
                    number: parseInt(cells[0]),
                    timestamp: cells[1],
                    user: cells[2],
                    action: cells[3],
                    target: cells[4],
                    details: cells[5],
                    metadata: {
                        timestamp: cells[1],
                        userId: cells[2],
                        device: { type: 'web', id: '', platform: '' },
                        network: { ipAddress: '' },
                        consent: false,
                    },
                });
            }
        }
    }
    return entries;
}
export function serializeFrontMatter(data) {
    const lines = ['---'];
    lines.push(`project_id: ${data.project_id}`);
    lines.push(`name: "${data.name}"`);
    lines.push(`workflow: ${data.workflow}`);
    lines.push(`default_branch: ${data.default_branch}`);
    lines.push(`created: ${data.created}`);
    lines.push(`updated: ${data.updated}`);
    lines.push(`status: ${data.status}`);
    if (data.tags && data.tags.length > 0) {
        lines.push(`tags: [${data.tags.join(', ')}]`);
    }
    lines.push('config:');
    if (data.config.sprint_duration) {
        lines.push(`  sprint_duration: ${data.config.sprint_duration}`);
    }
    if (data.config.wip_limit) {
        lines.push(`  wip_limit: ${data.config.wip_limit}`);
    }
    if (data.config.story_point_scale) {
        lines.push(`  story_point_scale: [${data.config.story_point_scale.join(', ')}]`);
    }
    if (data.config.timezone) {
        lines.push(`  timezone: "${data.config.timezone}"`);
    }
    if (data.config.states && data.config.states.length > 0) {
        lines.push('  states:');
        for (const s of data.config.states) {
            lines.push(`    - id: ${s.id}`);
            lines.push(`      name: ${s.name}`);
            lines.push(`      color: ${s.color}`);
            lines.push(`      order: ${s.order}`);
        }
    }
    lines.push('---');
    return lines.join('\n');
}
export function serializeTeam(members) {
    const lines = ['## Team', ''];
    lines.push('| Name | Role | Username | Availability |');
    lines.push('|------|------|----------|--------------|');
    for (const m of members) {
        lines.push(`| ${m.name} | ${m.role} | ${m.username} | ${m.availability || '—'} |`);
    }
    return lines.join('\n');
}
export function serializeTask(task) {
    const lines = [];
    lines.push(`### ${task.id}: ${task.title} {#${task.id.toLowerCase()}}`);
    lines.push('');
    lines.push('| Field | Value |');
    lines.push('|-------|-------|');
    lines.push(`| Status | ${task.status} |`);
    lines.push(`| Priority | ${task.priority} |`);
    if (task.assignee)
        lines.push(`| Assignee | ${task.assignee} |`);
    if (task.storyPoints)
        lines.push(`| Story Points | ${task.storyPoints} |`);
    if (task.sprint)
        lines.push(`| Sprint | ${task.sprint} |`);
    if (task.labels.length > 0)
        lines.push(`| Labels | ${task.labels.join(', ')} |`);
    lines.push(`| Created | ${task.created} |`);
    lines.push(`| Updated | ${task.updated} |`);
    if (task.started)
        lines.push(`| Started | ${task.started} |`);
    if (task.lastUpdatedBy)
        lines.push(`| Last Updated By | ${task.lastUpdatedBy} |`);
    lines.push('');
    if (task.description) {
        lines.push('#### Description');
        lines.push('');
        lines.push(task.description);
        lines.push('');
    }
    if (task.acceptanceCriteria.length > 0) {
        lines.push('#### Acceptance Criteria');
        lines.push('');
        for (const ac of task.acceptanceCriteria) {
            lines.push(`- [${ac.completed ? 'x' : ' '}] ${ac.text}`);
        }
        lines.push('');
    }
    lines.push('---');
    return lines.join('\n');
}
export function serializeChangeLogEntry(entry) {
    return `| ${entry.number} | ${entry.timestamp} | ${entry.user} | ${entry.action} | ${entry.target} | ${entry.details} | device: ${entry.metadata.device.type}, ip: ${entry.metadata.network.ipAddress} |`;
}
//# sourceMappingURL=parser.js.map